import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2,
  Circle,
  User,
  BookOpen,
  Image,
  Upload,
  ChevronRight,
  X,
  Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const API = '/api';

const OnboardingChecklist = ({ onDismiss, compact = false }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/onboarding/creator-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
      
      // Track checklist view
      await axios.post(`${API}/onboarding/track-event`, {
        event_type: 'checklist_viewed'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {}); // Silent fail for tracking
    } catch (error) {
      console.error('Error fetching onboarding status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const trackCTAClick = async (ctaType) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/onboarding/track-event`, {
        event_type: 'cta_clicked',
        cta_type: ctaType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Silent fail for tracking
    }
  };

  const handleAction = async (action, route) => {
    await trackCTAClick(action);
    navigate(route);
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  // Don't show if dismissed, loading, or user is fully activated
  if (dismissed || loading || !status) return null;
  if (status.activated && status.checklist.profile_complete) return null;

  const { checklist, content_counts } = status;
  const completedSteps = Object.values(checklist).filter(Boolean).length;
  const totalSteps = Object.keys(checklist).length;
  const progressPercent = Math.round((completedSteps / totalSteps) * 100);

  const checklistItems = [
    {
      id: 'profile',
      label: 'Complete Your Profile',
      description: 'Add name and avatar',
      completed: checklist.profile_complete,
      action: 'complete_profile',
      route: '/profile/editor/creator',
      icon: User
    },
    {
      id: 'content',
      label: 'Create Your First Content',
      description: 'Series, Novel, or Artwork',
      completed: checklist.content_created,
      action: 'create_content',
      route: '/create-series',
      icon: BookOpen
    },
    {
      id: 'cover',
      label: 'Upload a Cover Image',
      description: 'Make your content stand out',
      completed: checklist.cover_uploaded,
      action: 'upload_cover',
      route: content_counts.series > 0 ? '/creator/dashboard' : '/create-series',
      icon: Image
    },
    {
      id: 'publish',
      label: 'Publish Your First Chapter',
      description: 'Share your work with the world',
      completed: checklist.chapter_published,
      action: 'publish_chapter',
      route: content_counts.series > 0 ? '/creator/dashboard' : '/create-series',
      icon: Upload
    }
  ];

  if (compact) {
    return (
      <Card className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border-orange-500/30" data-testid="onboarding-checklist-compact">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-semibold text-white">Getting Started</span>
            </div>
            <span className="text-xs text-orange-400">{completedSteps}/{totalSteps}</span>
          </div>
          
          {/* Progress bar */}
          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden mb-3">
            <div 
              className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Next incomplete item */}
          {checklistItems.filter(item => !item.completed)[0] && (
            <Button
              size="sm"
              className="w-full bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border border-orange-500/30"
              onClick={() => {
                const nextItem = checklistItems.find(item => !item.completed);
                if (nextItem) handleAction(nextItem.action, nextItem.route);
              }}
            >
              {checklistItems.find(item => !item.completed)?.label}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-zinc-900 to-zinc-800 border-orange-500/30" data-testid="onboarding-checklist">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-orange-400" />
            Getting Started Checklist
          </CardTitle>
          <button
            onClick={handleDismiss}
            className="text-zinc-500 hover:text-white transition-colors"
            aria-label="Dismiss checklist"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Progress bar */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
            <span>{completedSteps} of {totalSteps} complete</span>
            <span>{progressPercent}%</span>
          </div>
          <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-2">
        <div className="space-y-2">
          {checklistItems.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.id}
                className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                  item.completed 
                    ? 'bg-green-500/10 border border-green-500/20' 
                    : 'bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 cursor-pointer'
                }`}
                onClick={() => !item.completed && handleAction(item.action, item.route)}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    item.completed ? 'bg-green-500/20' : 'bg-orange-500/10'
                  }`}>
                    <Icon className={`w-4 h-4 ${
                      item.completed ? 'text-green-400' : 'text-orange-400'
                    }`} />
                  </div>
                  <div>
                    <p className={`font-medium ${
                      item.completed ? 'text-green-400' : 'text-white'
                    }`}>
                      {item.label}
                    </p>
                    <p className="text-xs text-zinc-500">{item.description}</p>
                  </div>
                </div>
                
                {item.completed ? (
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-zinc-500" />
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default OnboardingChecklist;
